import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import { Ref } from "semantic-ui-react";
import { validate as uuidValidate } from "uuid";

import ProposalSummary from "components/ProposalSummary";
import RelatedRecords from "components/RelatedRecords";
import Tabs from "components/Tab/Tabs";
import ProposalAdminOnlyView from "containers/Proposal/Detail/AdminOnly";
import ProposalAdminTeamView from "containers/Proposal/Detail/AdminTeam";
import ProposalAttachmentsView from "containers/Proposal/Detail/Attachments";
import ProposalHistoryView from "containers/Proposal/Detail/History";
import ProposalNotesView from "containers/Proposal/Detail/Notes";
import ProposalRouting from "containers/Proposal/Detail/Routing";
import {
	PROJECTS_BASE_ROUTE,
	PROJECT_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	PROJECT_PROPOSAL_ADMINISTRATOR_ASSIGNED,
	FORM_SUBMISSION_TYPES,
} from "globals/constants";
import { useDictionary, useHasRoles } from "hooks";
import { getProposal, resetProposal } from "store/actions/proposal";
import { proposalSelectors } from "store/selectors";
import { spUrl } from "utils";

import ProposalFormView from "./Form";
import styles from "./styles.css";
import ProposalTasksView from "./Tasks/ProposalTasksView";


const ProposalDetailView = () => {
	const proposal = useSelector(proposalSelectors.proposal);

	const [ justActivatedSectionLabel, setJustActivatedSectionLabel ] = useState("");
	const [ isFormComplete, setIsFormComplete ] = useState(false);
	const [ isFormLoading, setIsFormLoading ] = useState(false); // todo: get from redux
	const [ isUploading, setIsUploading ] = useState(false); // todo: get from redux
	const dictionary = useDictionary("proposalDetails");
	const tabsRef = useRef(null);
	const { proposalId } = useParams();
	const history = useHistory();

	useEffect(() => {
		resetProposal();

		if (uuidValidate(proposalId)) {
			getProposal(proposalId);
		} else {
			history.push("/sp/proposals");
		}
	}, [ history, proposalId ]);

	const handleFormLoaded = useCallback(formCompleted => {
		setIsFormLoading(false);
		setIsFormComplete(formCompleted);
	}, []);

	const breadcrumbs = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary?.breadcrumbRoot || "My Active Projects",
				link: spUrl("/"),
			},
			{
				key: 1,
				text: proposal?.projectTitle,
				link: spUrl(`${PROJECTS_BASE_ROUTE}/${proposal?.projectId}`),
			},
			{
				key: 2,
				text: proposal?.proposalNumber,
				link: spUrl(`/sp/proposals/${proposal?.proposalId}`),
			},
		]}
		/>
	);

	const {
		[PROJECT_PROPOSAL_ADMINISTRATOR]: isProposalAdmin,
		[PROJECT_PROPOSAL_ADMINISTRATOR_ASSIGNED]: isProposalAdminAssigned,
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isSpProposalAdmin,
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
	} = useHasRoles([
		PROJECT_PROPOSAL_ADMINISTRATOR,
		PROJECT_PROPOSAL_ADMINISTRATOR_ASSIGNED,
		ROLE_SP_PROPOSAL_ADMINISTRATOR,
		ROLE_SP_SYSTEM_ADMINISTRATOR,
	]);
	const showAdminOnlyTab = isSystemAdmin || isProposalAdmin || isSpProposalAdmin || isProposalAdminAssigned;

	const panes = [ // todo: remember which tab was open
		{
			menuItem: dictionary.tabLabels?.form || "Proposal Form",
			render: () => (
				<div className={styles.proposalDetailsFormTab} loading={isFormLoading}>
					<ProposalFormView
						isFormComplete={isFormComplete}
						setIsFormComplete={setIsFormComplete}
						handleFormLoaded={handleFormLoaded}
						justActivatedSectionLabel={justActivatedSectionLabel}
						setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					/>
				</div>
			),
			ref: useRef(null),
			className: "form",
		},
		{
			menuItem: dictionary.tabLabels?.routing || "Routing",
			render: () => <ProposalRouting />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.history || "History",
			render: () => <ProposalHistoryView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.access || "Access",
			render: () => <ProposalAdminTeamView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.tasks || "Tasks",
			render: () => <ProposalTasksView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.notes || "Notes",
			render: () => <ProposalNotesView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.attachments || "Attachments",
			render: () => (
				<ProposalAttachmentsView
					tabsRef={tabsRef}
					formTabText={dictionary.tabLabels?.form}
					setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					setIsUploading={setIsUploading}
					isUploading={isUploading}
				/>
			),
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.relatedRecords || "Related Records",
			render: () => <RelatedRecords formType={FORM_SUBMISSION_TYPES.PROPOSAL} />,
			ref: useRef(null),
		},

	];

	const adminOnly = {
		menuItem: dictionary.tabLabels?.adminOnly || "Admin Only",
		render: () => <ProposalAdminOnlyView setIsUploading={setIsUploading}/>,
		ref: useRef(null),
	};

	if(showAdminOnlyTab) {
		panes.push(adminOnly);
	}

	return (
		<div>
			<div className={styles.sectionHeaderWrapper}>
				{proposal.isFetching && !proposal.isRefresh && (
					<div className={styles.sectionHeaderLoader}>
						<LoadingIndicator/>
					</div>
				)}
				<SectionHeader header={proposal.projectTitle} breadcrumbs={breadcrumbs} />
			</div>

			<BasePadding>
				<ProposalSummary isFormComplete={isFormComplete} />

				<Ref innerRef={tabsRef}>
					<Tabs
						className={styles.proposalDetailsTabs}
						panes={panes}
						disabled={isUploading}
					/>
				</Ref>
			</BasePadding>

		</div>
	);
};

export default ProposalDetailView;
